// Browser-side storage for the /linux page, shared with Settings > Linux (usage + delete-all).
//   localStorage (revault:linux-*): machine list, active machine, CheerpX licence record - small,
//     so they ride along in Re:Vault backups like every other revault:* key.
//   IndexedDB "revault-linux": v86 snapshots and uploaded ISO/IMG files (tens of MB each; never
//     in Cache Storage, never in backups).
//   IndexedDB "cjFS_/revault-cx-*": CheerpX's own disk databases, named by the runtime.
// Read-modify-write of shared records goes through a Web Lock so two tabs can't lose an update.
import { remove } from "./store.js";

export const MACHINES_KEY = "revault:linux-machines";
export const ACTIVE_KEY = "revault:linux-active";
export const CHEERPX_KEY = "revault:linux-cheerpx";
export const RESET_KEY = "revault:linux-cx-reset"; // pending CheerpX disk reset (see cheerpx/adapter.js)
const DB = "revault-linux";
const CX_DB_PREFIX = "cjFS_/revault-cx-";
// Free space kept back when checking whether a snapshot or upload fits.
export const QUOTA_MARGIN = 32 * 1024 * 1024;

let dbp = null;
function openDB() {
	if (dbp) return dbp;
	dbp = new Promise((resolve, reject) => {
		const r = indexedDB.open(DB, 1);
		r.onupgradeneeded = () => {
			const db = r.result;
			if (!db.objectStoreNames.contains("snapshots")) db.createObjectStore("snapshots");
			if (!db.objectStoreNames.contains("images")) db.createObjectStore("images");
		};
		r.onsuccess = () => {
			const db = r.result;
			// Let "delete all Linux data" (possibly from another tab) close us instead of blocking.
			db.onversionchange = () => { try { db.close(); } catch (e) {} dbp = null; };
			resolve(db);
		};
		r.onerror = () => { dbp = null; reject(r.error || new Error("IndexedDB unavailable")); };
		r.onblocked = () => { dbp = null; reject(new Error("Linux storage is busy in another tab")); };
	});
	return dbp;
}
const done = (req) => new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });
const committed = (tx) => new Promise((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error || new Error("aborted")); });

async function put(store, key, value) {
	const tx = (await openDB()).transaction(store, "readwrite");
	tx.objectStore(store).put(value, key);
	await committed(tx); // resolve only once it is durably committed
}
async function get(store, key) {
	return done((await openDB()).transaction(store).objectStore(store).get(key));
}
async function del(store, key) {
	const tx = (await openDB()).transaction(store, "readwrite");
	tx.objectStore(store).delete(key);
	await committed(tx);
}
async function entries(store) {
	const s = (await openDB()).transaction(store).objectStore(store);
	const [keys, values] = await Promise.all([done(s.getAllKeys()), done(s.getAll())]);
	return keys.map((k, i) => [k, values[i]]);
}

// Snapshots: { data: Blob, meta: string (compat identity), bytes, savedAt }.
export const getSnapshot = (id) => get("snapshots", id);
export const putSnapshot = (id, rec) => put("snapshots", id, rec);
export const deleteSnapshot = (id) => del("snapshots", id);
// Uploaded images: { blob: Blob, name, size, addedAt }.
export const getImage = (id) => get("images", id);
export const putImage = (id, rec) => put("images", id, rec);
export const deleteImage = (id) => del("images", id);
export async function listImages() {
	return (await entries("images")).map(([id, v]) => ({ id, name: v.name, size: v.size, addedAt: v.addedAt }));
}

export async function estimate() {
	try {
		const e = await navigator.storage.estimate();
		return e && typeof e.quota === "number" && typeof e.usage === "number" ? { usage: e.usage, quota: e.quota } : null;
	} catch (e) { return null; }
}
// Pure: true when `bytes` plus the margin fit in the remaining quota. An unknown estimate counts
// as "fits" - the write itself still fails cleanly (and callers keep the VM running if it does).
export function fitsInQuota(est, bytes, margin = QUOTA_MARGIN) {
	if (!est) return true;
	return est.quota - est.usage >= bytes + margin;
}

async function cheerpxDatabases() {
	try { return (await indexedDB.databases()).map((d) => d.name).filter((n) => n && n.startsWith(CX_DB_PREFIX)); }
	catch (e) { return []; }
}

// What Linux keeps in this browser. CheerpX sizes aren't exposed per database, so those are
// counted, and the origin-wide estimate is shown alongside for context.
export async function usage() {
	let snapshots = 0, images = 0, snapshotCount = 0, imageCount = 0;
	try {
		for (const [, v] of await entries("snapshots")) { snapshots += v.bytes || 0; snapshotCount++; }
		for (const [, v] of await entries("images")) { images += v.size || 0; imageCount++; }
	} catch (e) {}
	return { snapshots, images, snapshotCount, imageCount, cheerpxDisks: (await cheerpxDatabases()).length, estimate: await estimate() };
}

function deleteDatabase(name) {
	return new Promise((resolve) => {
		const r = indexedDB.deleteDatabase(name);
		r.onsuccess = () => resolve(true);
		r.onerror = () => resolve(false);
		// A running machine in another tab still holds it open; report instead of hanging.
		r.onblocked = () => resolve(false);
	});
}

// Settings > Linux > Delete all Linux data. Returns the databases that could not be removed
// (a machine is still running somewhere) so the UI can say so instead of pretending.
export async function deleteAllLinuxData() {
	try { if (dbp) (await dbp).close(); } catch (e) {}
	dbp = null;
	const failed = [];
	for (const name of [DB, ...(await cheerpxDatabases())]) if (!(await deleteDatabase(name))) failed.push(name);
	for (const k of [MACHINES_KEY, ACTIVE_KEY, CHEERPX_KEY, RESET_KEY]) remove(k); // remove() also tells other tabs
	return { failed };
}

export const cheerpxDbName = (device) => "cjFS_/" + device + "/";
export { deleteDatabase };
